import { Box, CircularProgress } from "@mui/material";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useAuth } from "src/utils/useAuth";

interface ProtectedRouteProps {
  children: ReactNode;
}

const Spinner = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
    }}
  >
    <CircularProgress />
  </Box>
);

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isEnabled, isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } =
    useAuth();

  // null = in-flight | true = confirmed valid | false = broken, redirect triggered
  const [tokenVerified, setTokenVerified] = useState<boolean | null>(null);

  useEffect(() => {
    // If Auth0 is enabled and user is not authenticated and not loading,
    // redirect to login
    if (isEnabled && !isAuthenticated && !isLoading) {
      // Preserve full path including search params and hash for deep linking
      const returnTo = window.location.pathname + window.location.search + window.location.hash;
      loginWithRedirect({
        appState: { returnTo },
      });
    }
  }, [isEnabled, isAuthenticated, isLoading, loginWithRedirect]);

  useEffect(() => {
    if (!isEnabled || isLoading || !isAuthenticated) {
      setTokenVerified(null); // reset so re-verification runs after next auth state change
      return;
    }

    let cancelled = false;
    const verifyToken = async () => {
      try {
        await getAccessTokenSilently();
        if (!cancelled) setTokenVerified(true);
      } catch {
        if (!cancelled) {
          setTokenVerified(false);
          const returnTo = window.location.pathname + window.location.search + window.location.hash;
          loginWithRedirect({ appState: { returnTo } });
        }
      }
    };
    verifyToken();
    return () => {
      cancelled = true;
    };
  }, [isEnabled, isLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  if (!isEnabled) return <>{children}</>;

  // Spinner while: SDK initializing OR token verification in flight
  if (isLoading || (isAuthenticated && tokenVerified === null)) return <Spinner />;

  // Spinner while redirect is in flight (unauthenticated or broken token)
  if (!isAuthenticated || tokenVerified === false) return <Spinner />;

  return <>{children}</>;
}
